import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, FileSearch, Image as ImageIcon, Layers, Loader2, RefreshCw,
  Ruler, ShieldAlert, Users, Wand2,
} from "lucide-react";
import { api } from "@/lib/api";
import { billsRead, isFeatureOff } from "@/lib/billsNative";
import { useSession } from "@/contexts/SessionContext";

// Every genuine human decision in one inbox. Categories map to EXISTING backend state only —
// no state was invented for UI symmetry, and nothing is hidden in a backend-only queue.
const CATEGORIES = [
  { key: "NEEDS_REVIEW", label: "Bill review", icon: FileSearch, to: "/queue?tab=NEEDS_REVIEW",
    hint: "Captured bills waiting for a human decision" },
  { key: "NEEDS_OCR_REVIEW", label: "OCR review", icon: ImageIcon, to: "/queue?tab=NEEDS_OCR_REVIEW",
    hint: "Readable, but key fields need confirming" },
  { key: "OCR_FAILED", label: "OCR failed", icon: AlertTriangle, to: "/queue?tab=OCR_FAILED",
    hint: "The document could not be read" },
  { key: "VENDOR", label: "Vendor needed", icon: Users, to: "/vendors/resolve",
    hint: "Vendor identity must be resolved by a human" },
  { key: "INGREDIENT", label: "Ingredient needed", icon: Wand2, to: "/bills?view=attention",
    hint: "A purchase line has no canonical ingredient yet" },
  { key: "CONVERSION", label: "Conversion needed", icon: Ruler, to: "/bills?view=attention",
    hint: "Pack size or unit must be confirmed" },
  { key: "DUPLICATE", label: "Duplicate review", icon: Layers, to: "/queue?tab=BLOCKED",
    hint: "Possible duplicate of an existing bill" },
  { key: "BLOCKED", label: "Blocked", icon: ShieldAlert, to: "/queue?tab=BLOCKED",
    hint: "Blocked until something upstream is fixed" },
  { key: "CORRECTIONS", label: "Corrections", icon: RefreshCw, to: "/bills?view=confirmed",
    hint: "Confirmed bills with a correction in progress" },
  { key: "VOID", label: "VOID approvals", icon: ShieldAlert, to: "/bills?view=confirmed",
    hint: "A different authorised user must approve" },
];

export default function NeedsAttention() {
  const { currentOutletId } = useSession();
  const [state, setState] = useState("loading");
  const [counts, setCounts] = useState({});

  const load = useCallback(async () => {
    setState("loading");
    const [queue, home] = await Promise.all([
      api.candidateCounts().catch(() => ({})),
      billsRead.homeSummary({}),
    ]);
    if (home && !home.ok && (home.status === 503 || isFeatureOff(home))) {
      setState("unavailable");
      return;
    }
    const q = (queue && queue.ok ? queue.data : queue) || {};
    const h = (home && home.ok ? home.data : {}) || {};
    const a = h.needs_attention || {};
    setCounts({
      NEEDS_REVIEW: q.NEEDS_REVIEW ?? q.counts?.NEEDS_REVIEW ?? 0,
      NEEDS_OCR_REVIEW: q.NEEDS_OCR_REVIEW ?? q.counts?.NEEDS_OCR_REVIEW ?? 0,
      OCR_FAILED: q.OCR_FAILED ?? q.counts?.OCR_FAILED ?? 0,
      BLOCKED: q.BLOCKED ?? q.counts?.BLOCKED ?? 0,
      VENDOR: a.vendor_needed ?? null,
      INGREDIENT: a.ingredient_needed ?? null,
      CONVERSION: a.conversion_needed ?? null,
      DUPLICATE: null,
      CORRECTIONS: a.corrections_pending ?? 0,
      VOID: a.void_approvals ?? 0,
      BILLS_ATTENTION: a.bills_needing_decisions ?? 0,
    });
    setState("ready");
  }, []);

  useEffect(() => { if (currentOutletId) load(); }, [load, currentOutletId]);

  if (!currentOutletId) {
    return <p className="py-16 text-center text-[13px] text-archival-muted" data-testid="attention-no-outlet">
      Select an outlet to see what needs your attention.
    </p>;
  }

  if (state === "unavailable") {
    return (
      <div className="max-w-md mx-auto py-16 text-center" data-testid="attention-unavailable">
        <AlertTriangle className="mx-auto mb-3 text-amber-600" size={22} />
        <h1 className="text-[18px] font-semibold text-archival-primary">Needs attention is temporarily unavailable</h1>
        <p className="text-[13px] text-archival-secondary mt-1">
          We couldn&apos;t reach the Bills datastore. Your existing data has not been changed.
        </p>
        <button onClick={load} className="mt-4 rounded border border-archival px-3 py-1.5 text-[13px]"
          data-testid="attention-retry">Retry</button>
      </div>
    );
  }

  const total = Object.entries(counts)
    .filter(([k]) => k !== "BILLS_ATTENTION")
    .reduce((s, [, v]) => s + (Number(v) || 0), 0);

  return (
    <div className="space-y-6" data-testid="needs-attention">
      <header>
        <h1 className="text-[26px] md:text-[28px] font-semibold text-archival-primary tracking-tight leading-tight">
          Needs attention
        </h1>
        <p className="text-[14px] text-archival-secondary mt-1">
          Every decision 1OS Bills needs a human to make · {state === "ready" ? total : "…"} open
        </p>
      </header>

      {state === "loading" ? (
        <div className="py-16 text-center text-archival-muted">
          <Loader2 className="animate-spin mx-auto mb-2" size={18} /> Checking…
        </div>
      ) : total === 0 ? (
        <div className="rounded-md border border-archival bg-archival-surface py-16 text-center"
          data-testid="attention-empty">
          <p className="text-[14px] font-medium text-archival-primary">Nothing needs your attention.</p>
          <p className="text-[12px] text-archival-muted mt-1">
            New captures and ambiguous decisions will appear here.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="attention-categories">
          {CATEGORIES.map(({ key, label, icon: Icon, to, hint }) => {
            const n = counts[key];
            const known = n !== null && n !== undefined;
            return (
              <Link
                key={key}
                to={to}
                data-testid={`attention-card-${key.toLowerCase()}`}
                className={`rounded-md border p-4 min-h-[88px] flex items-start gap-3 transition-colors ${
                  known && Number(n) > 0
                    ? "border-archival bg-archival-surface hover:bg-archival-soft"
                    : "border-archival bg-archival-surface/60 hover:bg-archival-soft"
                }`}
              >
                <Icon size={16} className="mt-0.5 text-archival-muted shrink-0" />
                <span className="min-w-0">
                  <span className="flex items-baseline gap-2">
                    <span className="text-[20px] font-semibold text-archival-primary tabular-nums">
                      {known ? n : "—"}
                    </span>
                    <span className="text-[13px] font-medium text-archival-primary">{label}</span>
                  </span>
                  <span className="block text-[12px] text-archival-muted mt-0.5">{hint}</span>
                </span>
              </Link>
            );
          })}
        </div>
      )}

      <p className="text-[11px] text-archival-muted">
        Counts shown as “—” are tracked inside the bill itself rather than as a separate queue; open
        Bills · Needs attention to work through them.
      </p>
    </div>
  );
}
